import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';

import {
    DepartmentFoodActionsUnion,
    DepartmentFoodActionTypes
} from '@core/actions/department-food.actions';
import { DepartmentFood } from '@app/models/department-food';
import { comparerByAlphabet } from '@app/comparers';
import {
    AuthActionsUnion,
    AuthActionTypes
} from '@app/modules/core/actions/auth.actions';
import { createFeatureSelector, createSelector } from '@ngrx/store';

export interface State extends EntityState<DepartmentFood> {
    loading: boolean;
    creating: boolean;
    loaded: boolean;
    selectedId: number;
    filteredIds: any[];
}

export const adapter: EntityAdapter<DepartmentFood> = createEntityAdapter<
DepartmentFood
>({
    selectId: (departmentFood: DepartmentFood) => departmentFood.id,
    sortComparer: comparerByAlphabet('ASC', 'name')
});

export const INITIAL_STATE: State = adapter.getInitialState({
    loading: false,
    creating: false,
    loaded: false,
    selectedId: null,
    filteredIds: []
});

export function reducer(
    state: State = INITIAL_STATE,
    action: DepartmentFoodActionsUnion | AuthActionsUnion
) {
    switch (action.type) {
        case DepartmentFoodActionTypes.Load:
            return { ...state, loading: true };

        case DepartmentFoodActionTypes.LoadFail:
            return { ...state, loading: false };

        case DepartmentFoodActionTypes.LoadSuccess:
            return adapter.addAll(action.departmentsFood, {
                ...state,
                loading: false,
                loaded: true
            });

        case DepartmentFoodActionTypes.LoadByConditions:
            return { ...state, loading: true };

        case DepartmentFoodActionTypes.LoadByConditionsSuccess:
            return adapter.addAll(action.departmentsFood, {
                ...state,
                loading: false,
                loaded: true
            });

        case DepartmentFoodActionTypes.LoadByConditionsFail:
            return { ...state, loading: false };

        case DepartmentFoodActionTypes.Create:
            return { ...state, creating: true };

        case DepartmentFoodActionTypes.CreateFail:
            return { ...state, creating: false };

        case DepartmentFoodActionTypes.CreateSuccess:
            return adapter.addOne(action.departmentsFood, {
                ...state,
                creating: false
            });

        case DepartmentFoodActionTypes.Select:
            return { ...state, selectedId: action.id };

        // KEEP DEPARTMENTS LOADED ON LOGOUT
        case AuthActionTypes.Logout:
            return { ...state, selectedId: null };

        case DepartmentFoodActionTypes.Search:
            return {
                ...state,
                filteredIds: [],
                loading: true
            };

        case DepartmentFoodActionTypes.SearchSuccess:
            return {
                ...state,
                filteredIds:
                    action.DepartmentIds.length === 0 ? [-1] : action.DepartmentIds,
                loading: false
            };

        case DepartmentFoodActionTypes.SearchFail:
            return {
                ...state,
                filteredIds: [],
                loading: false
            };

        case DepartmentFoodActionTypes.ClearSearch:
            return {
                ...state,
                filteredIds: [],
                loading: false
            };

        case DepartmentFoodActionTypes.LoadById:
            return { ...state, loading: true };

        case DepartmentFoodActionTypes.LoadByIdSuccess:
            let newFiltered = []
            if (state.filteredIds.length > 0) {

                let newData = state.filteredIds.filter(item => item.id === action.CategoryId)[0] = {
                    id: action.CategoryId,
                    ...action.data
                }
                newFiltered.push(newData)
                let currentData = state.filteredIds.filter(item => item.id !== action.CategoryId)
                newFiltered.push(...currentData)
            }

            return adapter.updateOne({ id: action.CategoryId, changes: action.data },
                {
                    ...state,
                    filteredIds: newFiltered
                })

        case DepartmentFoodActionTypes.LoadByIdFail:
            return { ...state, loading: false };

        case DepartmentFoodActionTypes.Clear:
            return INITIAL_STATE;

        case DepartmentFoodActionTypes.DeleteSuccess:
            const DepartmentId = action.DepartmentId;

            return adapter.removeOne(DepartmentId, state);

        case DepartmentFoodActionTypes.UpdateSuccess:
            return adapter.upsertOne(action.department, state);
        default:
            return state;
    }
}

export const {
    selectIds,
    selectEntities,
    selectAll,
    selectTotal
} = adapter.getSelectors();

export const selectLoaded = (state: State) => state.loaded;
export const selectLoading = (state: State) => state.loading;
export const selectSelectedId = (state: State) => state.selectedId;
export const getState = createFeatureSelector<State>('departmentsFood');
export const selectDepartmentIds = (state: State) => state.filteredIds;

export const getFilteredDepartmentIds = createSelector(
    getState,
    selectDepartmentIds
);
